import { Inject, Injectable } from '@angular/core';
import { Integrationspartnersv1BatchGetAccountsRequest } from '@blk/portfolio-security-server/account';
import { V1ExternalAccountInfo, V1SearchExternalAccountInfosRequest } from '@blk/portfolio-security-server/accountInfo';
import { Partnersv1Platform, V1ExchangeAuthorizationCodeRequest, V1FilterPlatformsRequest } from '@blk/portfolio-security-server/platform';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { InjectConfig, PaConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig } from '@ddt-web/data-service';
import { PartnerPlatformControlsList } from '@ddt-web/domain';
import { PartnerAccount, PartnerPlatform } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { UrlService } from '@ddt-web/url';
import * as isEmpty from 'lodash/isEmpty';
import { Observable, of } from 'rxjs';
import { catchError, first, map, take, tap, withLatestFrom } from 'rxjs/operators';

import { PartnerPortfoliosApiService } from './partner-portfolios-api.service';

@Injectable({
	providedIn: 'root'
})
export class PartnerPortfoliosDataService {
	private static readonly AC_PKG = 'AC';

	@InjectConfig(PaConfigKey.PARTNER_PLATFORM_CONTROLS_LIST) public readonly partnerPlatformControlsList: PartnerPlatformControlsList;

	constructor(
		private partnerPortfoliosApiService: PartnerPortfoliosApiService,
		private storeService: StoreService,
		private urlService: UrlService,
		@Inject(APP_STORE_CONFIG_TOKEN) private storeConfig: AppStoreConfig
	) { }

	public retrievePartnerPlatformsList(): Observable<PartnerPlatform[]> {
		return this.storeService
			.selectAll(this.storeConfig.partnerPlatformList)
			.pipe(
				tap(partnerListConfig => {
					if (isEmpty(partnerListConfig.entities)) {
						this.initialisePartnerPlatformsConfig();
					}
				}),
				first(partnerListConfig => Boolean(!partnerListConfig.isLoading && partnerListConfig.entities)),
				map(partnerListConfig => Object.values(partnerListConfig.entities))
			);
	}

	public retrieveAndFilterPartnerList(isCompareMode: boolean): Observable<PartnerPlatform[]> {
		const urlIds = this.urlService.getPortfolioIds();
		return this.retrievePartnerPlatformsList().pipe(
			withLatestFrom(this.storeService.selectAll(this.storeConfig.associatedPortfolios)),
			map(([partnerList, associatedPortfolios]) => {
				const partnerId = urlIds.map(id => associatedPortfolios.entities[id]?.originPlatform?.platform).filter(platform => !!platform)?.[0];
				return partnerList
					.filter((config) => {
						let isAllowed = this.partnerPlatformControlsList?.[config.id]?.allowPortfolioImport;

						if (isAllowed && isCompareMode && partnerId) {
							isAllowed = (partnerId === config.id);
						}
						return isAllowed;
					})
					.map((partner) => { return { ...partner, isSelected: isCompareMode && partner.id === partnerId }; }); // to preselect the currently selected partner
			})
		);
	}

	private initialisePartnerPlatformsConfig(): void {
		const requestBody: V1FilterPlatformsRequest = {
			query: {
				pkg: PartnerPortfoliosDataService.AC_PKG
			}
		};
		this.partnerPortfoliosApiService.fetchPlatformList(requestBody)
			.pipe(first())
			.subscribe(({ platforms }: { platforms: Partnersv1Platform[] }) => {
				this.storeService.upsertMany(this.storeConfig.partnerPlatformList, platforms);
			}, error => {
				console.error(error);
			});
	}

	public exchangeAuthCode(authorizationCode: string, partnerPlatformId: string): Observable<boolean> {
		const requestBody: V1ExchangeAuthorizationCodeRequest = {
			authorizationCode,
			partnerPlatformId,
			pkg: PartnerPortfoliosDataService.AC_PKG
		};
		return this.partnerPortfoliosApiService.exchangeAuthCode(requestBody).pipe(first(), map(response => {
			return true;

		}), catchError(error => {
			console.error(error);
			return of(false);
		}));
	}

	public getPortfolioSearchList(platformId: string, searchCriteriaIds: string[], searchText: string): Observable<V1ExternalAccountInfo[]> {
		const requestBody: V1SearchExternalAccountInfosRequest = {
			platformId,
			pkg: PartnerPortfoliosDataService.AC_PKG,
			searchCriterias: searchCriteriaIds.map(criteriaId => (
				{
					field: criteriaId,
					value: searchText
				}
			))

		};
		return this.partnerPortfoliosApiService.portfolioSearch(requestBody).pipe(
			take(1),
			map(response => response.externalAccountInfos)
		);
	}

	public getAccountsBatchData(platformId: string, accountIdList: string[]): Observable<PartnerAccount[]> {
		const accounts = (accountIdList || []).map(id => {
			return {
				id,
				asOfTime: '2017-10-19T00:00:00.000Z', // this is temporary till the time backend makes this field optional
				includeLots: true
			};
		});
		const requestBody: Integrationspartnersv1BatchGetAccountsRequest = {
			platformId,
			pkg: PartnerPortfoliosDataService.AC_PKG,
			accounts
		};
		return this.partnerPortfoliosApiService.accountsBatchGet(requestBody).pipe(
			take(1),
			map(response => response?.accounts)
		);
	}
}
